<template>
  <div class="quality-breakdown-card">
    <div class="quality-breakdown-card__header">
      <h6 class="quality-breakdown-card__title">{{ quality.quality_name }}</h6>
    </div>

    <div class="quality-breakdown-card__content">
      <div class="quality-breakdown-card__chart-container">
        <donut-chart
          :labels="[quality.quality_name, 'Other']"
          :values="[quality.percentage, 100 - quality.percentage]"
          :colors="[color, PaletteColors['default-lighten-80']]"
          :show-data-labels="false"
          cutout="70%"
          :chart-options="{ aspectRatio: 1 }"
        />
        <div class="quality-breakdown-card__percentage">{{ quality.percentage }}%</div>
      </div>

      <div class="quality-breakdown-card__stat">
        <span class="quality-breakdown-card__stat-label">
          <span class="quality-breakdown-card__color-dot" :style="{ backgroundColor: color }"></span>
          {{ quality.quality_name }}
        </span>
        <span class="quality-breakdown-card__stat-value">{{ quality.percentage }}% of recognitions</span>
      </div>
    </div>

    <div v-if="topEmployees.length" class="quality-breakdown-card__employees">
      <p class="quality-breakdown-card__employees-title">Top {{ topEmployees.length }} employees</p>
      <div v-for="employee in topEmployees" :key="employee.employee_id" class="quality-breakdown-card__employee">
        <div class="quality-breakdown-card__employee-info">
          <span class="quality-breakdown-card__employee-name">{{ employee.full_name }}</span>
        </div>
        <span class="quality-breakdown-card__employee-count">{{ employee.recognitions_count }}</span>
      </div>
    </div>
    <b-spinner v-else-if="loadingTopEmployees" small class="quality-breakdown-card__spinner" />
  </div>
</template>

<script setup>
import axios from 'axios';
import * as Sentry from '@sentry/browser';
import DonutChart from '@/components/charts/DonutChart.vue';
import { PaletteColors } from '@/Theme';
import { getDatasetColor } from '@/utils/chart';
import ApiClient from '@/ApiClient';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  quality: {
    type: Object,
    required: true,
  },
  colorIndex: {
    type: Number,
    default: 0,
  },
  programIds: {
    type: Array,
    default: () => [],
  },
  params: {
    type: Object,
    default: () => ({}),
  },
});

const color = computed(() => PaletteColors[getDatasetColor(props.colorIndex)]);

const topEmployees = ref([]);
const loadingTopEmployees = ref(false);
let cancelSource = null;

const reportTopEmployeesError = err => {
  if (axios.isCancel(err)) {
    return;
  }
  console.error('[RecognitionAnalytics] value-distribution fetch failed', err);
  Sentry.addBreadcrumb({
    category: 'recognition_analytics',
    level: 'warning',
    message: err?.message || 'value-distribution fetch failed',
    data: {
      url: err?.config?.url,
      status: err?.response?.status,
      quality_name: props.quality?.quality_name,
    },
  });
};

const fetchTopEmployees = async () => {
  // Need both a value name and at least one program id to scope the request.
  if (!props.quality?.quality_name || !props.programIds.length) {
    topEmployees.value = [];
    return;
  }

  cancelSource?.cancel('superseded');
  cancelSource = axios.CancelToken.source();
  const token = cancelSource.token;

  loadingTopEmployees.value = true;
  try {
    // Path id = first program; pass full set as program_ids[] when rolling up multiple programs.
    const [primaryId, ...rest] = props.programIds;
    const requestParams = { ...props.params };
    if (rest.length > 0) {
      requestParams.program_ids = props.programIds;
    }

    const result = await ApiClient.getRecognitionAnalyticsValueDistribution(
      primaryId,
      props.quality.quality_name,
      requestParams,
      { cancelToken: token },
    );
    topEmployees.value = result?.top_employees ?? [];
  } catch (err) {
    reportTopEmployeesError(err);
    if (!axios.isCancel(err)) {
      topEmployees.value = [];
    }
  } finally {
    loadingTopEmployees.value = false;
  }
};

// Refetch whenever the program scope, filter params, or the rendered quality change.
// Vue may keep the card mounted across filter changes when quality_id is stable, so an
// onMounted hook isn't enough — we need a watcher on the inputs.
watch(
  () => [props.programIds, props.params, props.quality?.quality_name],
  () => fetchTopEmployees(),
  { immediate: true, deep: true },
);

onBeforeUnmount(() => {
  cancelSource?.cancel('component unmount');
});
</script>

<style scoped lang="scss">
.quality-breakdown-card {
  background-color: var(--palette-color-base-white);
  border-radius: 0.25rem;
  box-shadow: var(--depth-2);
  padding: 1.25rem;
  height: 100%;

  &__header {
    margin-bottom: 1rem;
  }

  &__title {
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0;
    color: var(--palette-color-default);
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__chart-container {
    position: relative;
    width: 80px;
    height: 80px;
    margin-bottom: 0.75rem;
  }

  &__percentage {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--palette-color-default);
  }

  &__stat {
    text-align: center;
  }

  &__stat-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--palette-color-default);
    margin-bottom: 0.25rem;
  }

  &__color-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__stat-value {
    font-size: 0.6875rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__employees {
    border-top: 1px solid var(--palette-color-default-lighten-90);
    padding-top: 0.75rem;
  }

  &__employees-title {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--palette-color-default-lighten-30);
    margin-bottom: 0.5rem;
  }

  &__employee {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0;

    &:not(:last-child) {
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }
  }

  &__employee-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  &__employee-name {
    font-size: 0.75rem;
    color: var(--palette-color-default);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__employee-count {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--palette-color-default);
    flex-shrink: 0;
  }

  &__spinner {
    display: block;
    margin: 0.5rem auto;
  }
}
</style>
